import React, { useCallback, useEffect, useState } from 'react'
import FlexRow from '../../../../../components/Layout/FlexRow'
import FlexCol from '../../../../../components/Layout/FlexCol'
import Preview from '../../../../../components/Preview'
import VariableUtils from '../../../../../utils/variable'
import './CreationsFormPercentageShareNumber.scss'
import Input from '../../../../../components/Form/Input'
import { InputMask } from '../../../../../utils/inputMask'
import { useTranslation } from 'react-i18next'
import debounce from 'lodash/debounce'
import { DEBOUNCE_POST_DELAY_DEFAULT } from '../../../../../constant'
import { CreationsFormPercentageShareNumberProps } from '../../models/types'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../../store'
import CreationsPercentageTooltipAlert from '../../../CampaignForm/CreationsForm/CreationPercentageTooltipAlert/CreationsEmissionsTooltipAlert'
import CreationsFormPercentageShareService from '../../services/creations-form-percentage-share.service'
import { campaignEmissionScheduleActions } from '../../store/campaign-emission-schedule-slice'

const CreationsFormPercentageShareNumber: React.FC<CreationsFormPercentageShareNumberProps> = ({
  creationId,
  creations,
  campaignEmissions,
  campaignAudience,
  target,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [errors, setErrors] = useState<string[]>([])
  const creation = creations.find(creation => creation.id === creationId)!
  const { readOnly, emissionNumber } = useSelector(
    (state: RootState) => state.campaignEmissionSchedule
  )

  const debouncedCall = useCallback(
    debounce(percentageShare => {
      return CreationsFormPercentageShareService.update(creation.id, percentageShare, setErrors, t)
    }, DEBOUNCE_POST_DELAY_DEFAULT),
    []
  )

  useEffect(() => {
    setErrors([])

    if (readOnly) {
      return
    }

    debouncedCall(emissionNumber.percentageShare)
  }, [emissionNumber.percentageShare, readOnly])

  useEffect(() => {
    if (creation.percentageShare) {
      const adjustedPercentageShare = (Number(creation.percentageShare) * 100).toString()

      dispatch(
        campaignEmissionScheduleActions.setEmissionsNumber({
          key: 'percentageShare',
          value: adjustedPercentageShare,
        })
      )
    }
  }, [creation])

  return (
    <FlexRow className='CreationsFormPercentageShareNumber--row'>
      <FlexCol>
        <Preview
          className={'CreationsFormPercentageShareNumber--preview'}
          src={creation.file.url}
          thumbnailSrc={creation.thumbnailUrl}
          isVideo={creation.file.isVideo}
          alt={creation.file.filename}
        />
      </FlexCol>
      <FlexCol>
        <div className={'CreationsFormPercentageShareNumber__emissions'}>
          <p>
            {t('form.campaign.details.target')}:
            <span className={'CreationsFormPercentageShareNumber__emissions--number'}>
              &nbsp;
              {target === 'audience'
                ? VariableUtils.formatNumber(campaignAudience)
                : VariableUtils.formatNumber(campaignEmissions)}
            </span>
          </p>
          <div className={'CreationsFormPercentageShareNumber__emissions--label'}>
            <span>
              {t('form.campaign.creations.creationPercentage')}:&nbsp;
              <b>{creation.file.filename}</b>
            </span>
            <CreationsPercentageTooltipAlert />
          </div>
          <Input
            id='percentage'
            title={''}
            help={t('form.campaign.details.emissionsHelp')}
            dataCy={'percentage-share-input'}
            value={emissionNumber.percentageShare}
            onChange={(value: string) => {
              dispatch(
                campaignEmissionScheduleActions.setEmissionsNumber({
                  key: 'percentageShare',
                  value,
                })
              )
            }}
            maskType={InputMask.INTEGER}
            errors={errors}
            readOnly={!!readOnly}
          />
        </div>
      </FlexCol>
    </FlexRow>
  )
}

export default CreationsFormPercentageShareNumber
